import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, Topic, Game } from '../api/rest';
import { currentIdToken, signOut } from '../auth/cognito';
import { relativeTime } from '../lib/time';
import { useT } from '../i18n';

export function AdminGames() {
  const t = useT();
  const navigate = useNavigate();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [topicSearch, setTopicSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void currentIdToken().then((t) => {
      if (!t) {
        navigate('/admin', { replace: true });
        return;
      }
      void refresh();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    try {
      const [ts, gs] = await Promise.all([api.listTopics(), api.listGames()]);
      setTopics(ts);
      setGames(gs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
  };

  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const removeGame = async (g: Game) => {
    if (!confirm(t('games.deleteConfirm', { code: g.code }))) return;
    try {
      await api.deleteGame(g.id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete game');
    }
  };

  const create = async () => {
    if (selected.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const game = await api.createGame(selected);
      navigate(`/admin/console/${game.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create game');
    } finally {
      setBusy(false);
    }
  };

  const ts = topicSearch.trim().toLowerCase();
  const visibleTopics = [...topics]
    .filter((t) => t.title.toLowerCase().includes(ts))
    .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));

  return (
    <div className="stack">
      <div className="rowbetween">
        <h1>{t('games.title')}</h1>
        <div className="row">
          <Link to="/admin/templates" className="ghost">{t('games.templates')}</Link>
          <Link to="/admin/authoring" className="ghost">{t('games.authoring')}</Link>
          <Link to="/history" className="ghost">{t('games.history')}</Link>
          <button className="ghost" onClick={() => signOut().then(() => navigate('/admin'))}>{t('games.signout')}</button>
        </div>
      </div>

      {error && <div className="banner error">{t(error)}</div>}

      <div className="card">
        <div className="section-head"><h2>{t('games.newGame')}</h2></div>
        <p className="muted">{t('games.pickTopics')}</p>
        {topics.length === 0 ? (
          <p className="muted empty">{t('games.noTopics')} <Link to="/admin/authoring">{t('games.createTopics')}</Link></p>
        ) : (
          <>
            {topics.length > 6 && (
              <div className="toolbar">
                <div className="searchbox">
                  <span className="searchicon">🔍</span>
                  <input value={topicSearch} onChange={(e) => setTopicSearch(e.target.value)} placeholder={t('games.filterTopics')} />
                </div>
              </div>
            )}
            <ul className="selectlist">
              {visibleTopics.map((topic) => {
                const idx = selected.indexOf(topic.id);
                const rel = relativeTime(topic.createdAt);
                return (
                  <li key={topic.id} className={idx >= 0 ? 'selected' : ''} onClick={() => toggle(topic.id)}>
                    <span className="ttitle">{topic.title}</span>
                    {rel && <span className="muted small when">{rel}</span>}
                    {idx >= 0 && <span className="order">#{idx + 1}</span>}
                  </li>
                );
              })}
              {visibleTopics.length === 0 && <li className="muted">{t('games.noMatch')}</li>}
            </ul>
          </>
        )}
        <button className="primary" disabled={selected.length === 0 || busy} onClick={create}>
          {busy ? t('games.creating') : t('games.createGame', { n: selected.length })}
        </button>
      </div>

      <div className="card">
        <h2>{t('games.recentGames')}</h2>
        {games.length === 0 ? (
          <p className="muted">{t('games.noGames')}</p>
        ) : (
          <ul className="gamelist">
            {games.map((g) => (
              <li key={g.id}>
                <span className="code">{g.code}</span>
                <span className={`status ${g.status}`}>{t(`status.${g.status}`)}</span>
                <span className="muted small when">{relativeTime(g.createdAt) ?? ''}</span>
                {g.status !== 'finished' ? (
                  <Link to={`/admin/console/${g.id}`}>{t('games.openConsole')}</Link>
                ) : (
                  <Link to="/history">{t('games.viewResults')}</Link>
                )}
                <button className="danger small" onClick={() => removeGame(g)}>{t('common.delete')}</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
